/**
 * AttractionsDetailViewCommon Component
 *
 * This component is used to display the common elements of an attractions detail screen across different platforms.
 * It handles data display such as the attractions's name, date, time, and location.
 *
 * Props:
 *  - route: Navigation route that includes attractions details.
 *  - FontAwesomeIcon: The FontAwesome icon component used for displaying icons.
 *  - iconSize: The size of the FontAwesome icon.
 *
 * @file AttractionsDetailViewCommon.js
 * @module AttractionsDetailViewCommon
 */
import React, { useState, useEffect } from "react";
import { View, StyleSheet, Platform, Linking } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { Text } from "react-native-paper";
import { COLORS, STRINGS } from "../../../constants";
import Heading from "../../../components/Heading/Heading";
import {
  faCircleInfo,
  faClock,
  faDirections,
  faGlobe,
  faLocationDot,
  faPhone,
  faShareAlt,
} from "@fortawesome/free-solid-svg-icons";
import SummaryCard from "../../../components/SummaryCard/SummaryCard";
import DetailButtonArray from "../../../components/DetailButtonArray/DetailButtonArray";
import DetailDescription from "../../../components/DetailDescription/DetailDescription";
import Chip from "../../../components/Chip/Chip";
import DetailList from "../../../components/DetailList/DetailList";
import DetailsMapView from "../../Other/DetailsMapView/DetailsMapView";
import { formatAddress } from "../../../utils/formatting";
import AttractionsStore from "../../../storage/attractionsStore";
import { getCurrentLocation, getDistanceFromLocation } from "../../../utils/geolocation";
import { share, supportsShare, openDirections } from "../../../utils/helper";
import Loading from "../../../components/Loading/Loading";
import PageNotFound from "../../../components/NoResults/PageNotFound";
import { logAnalyticsEvent } from "../../../utils/firebase";
import useMetaDescriptionOnFocus from "../../../components/Meta/MetaDescription";

const AttractionsDetailViewCommon = ({ route, FontAwesomeIcon, iconSize }) => {
  logAnalyticsEvent("screen_view", { name: "VBGo-AttractionsDetailView" });

  const navigation = useNavigation();
  const [distance, setDistance] = useState(null);
  const attractions = AttractionsStore((state) => state.attractions);
  const attractionIndex = route.params?.index;
  const attraction = attractions[attractionIndex];
  const loadingAttractions = AttractionsStore((state) => state.loadingAttractions);
  useMetaDescriptionOnFocus(attraction?.name);

  useEffect(() => {
    if (!attraction) return;
    const getDistance = async () => {
      try {
        const userLocation = await getCurrentLocation();
        setDistance(
          await getDistanceFromLocation(userLocation, { lat: attraction.latitude, lng: attraction.longitude })
        );
      } catch (err) {
        return null;
      }
    };
    getDistance();

    navigation.setOptions({
      title: attraction?.name,
      headerRight: () => (
        <DetailButtonArray
          FontAwesomeIcon={FontAwesomeIcon}
          buttons={initHeaderButtons()}
          onPress={handlePress}
          iconSize={Platform.OS === "web" ? "2x" : iconSize}
        />
      ),
      headerRightContainerStyle: styles.headerRight,
      headerTitleStyle: styles.headerTitle,
    });
  }, [attraction]);

  if (loadingAttractions) {
    return (
      <View style={styles.container}>
        <Loading />
      </View>
    );
  }

  if (!attraction) {
    return (
      <View style={styles.container}>
        <PageNotFound />
      </View>
    );
  }
  /**
   * Initializes an array of header button configurations based on the available attraction data.
   *
   * Each button object may include:
   * - Directions: If latitude and longitude are present.
   * - Website: If a URL is present.
   * - Call: If a phone number is present.
   * - Share: If sharing is supported.
   *
   * @returns {Array<Object>} Array of button configuration objects for the header.
   */
  const initHeaderButtons = () => {
    return [
      attraction.latitude && attraction.longitude
        ? { icon: faDirections, label: "Directions", color: COLORS.border, testID: "directions-button" }
        : null,
      attraction.url ? { icon: faGlobe, label: "Website", color: COLORS.border, testID: "website-button" } : null,
      attraction.phone ? { icon: faPhone, label: "Call", color: COLORS.border, testID: "call-button" } : null,
      supportsShare() ? { icon: faShareAlt, label: "Share", color: COLORS.border, testID: "share-button" } : null,
    ].filter(Boolean);
  };

  /**
   * handlePress
   *
   * This function is a simple handler for button press events. It logs the label of the pressed
   * button to the console for debugging or action tracking purposes.
   *
   * Props:
   * - button (object): The button object containing the `label` property to be logged.
   *
   * Returns:
   * - None (This function performs a console log as an action).
   */
  const handlePress = (button) => {
    const { label } = button;

    switch (label) {
      case "Directions": {
        openDirections(attraction.latitude, attraction.longitude);
        break;
      }

      case "Share": {
        const url = `\n\n${process.env.REACT_APP_APPURL}/attractions/details?index=${attractionIndex}`;
        const message = `Check out ${attraction.name} at ${formatAddress(
          attraction.address,
          attraction.city,
          attraction.zip
        )}. ${url}`;
        share(message, attraction.name);
        break;
      }

      case "Call": {
        Linking.openURL(`tel:${attraction.phone}`);
        break;
      }

      case "Website": {
        Linking.openURL(attraction.url);
        break;
      }

      default:
        console.log(`Button pressed: ${label}`);
    }
  };

  /**
   * renderItemDetails
   *
   * This function renders the details of tips inside attraction item in a specific format.
   *
   * Props:
   * - item (object): The tips data
   *
   * Returns:
   * - A Text component displaying the tips.
   */
  const renderItemDetails = (item) => <Text style={styles.parkingDetails}>{item || "No details available"}</Text>;
  const HeaderComponent = () => (
    <View>
      <View style={styles.mapContainer}>
        <DetailsMapView
          name={attraction?.name}
          lat={parseFloat(attraction?.latitude)}
          lng={parseFloat(attraction?.longitude)}
          screen={route.name}
        />
      </View>
      <Heading text={attraction.name} fontSize={26} />
      <View style={styles.chipContainer}>
        {distance != null && (
          <Chip
            FontAwesomeIcon={FontAwesomeIcon}
            icon={faLocationDot}
            title={`${distance} ${STRINGS.milesAway}`}
            size={iconSize}
            backgroundColor={COLORS.backgroundColor}
            borderColor={COLORS.border}
            iconColor={COLORS.textDark}
          />
        )}
      </View>
      <View style={styles.cardsContainer}>
        <SummaryCard
          heading={attraction?.name || "Location details unavailable"}
          description={formatAddress(attraction?.address, attraction?.city, attraction?.zip) || "No address provided."}
          icon={faLocationDot}
          size={iconSize}
          iconColor={COLORS.primary}
          FontAwesomeIcon={FontAwesomeIcon}
        />
        {attraction?.hours?.length && (
          <SummaryCard
            heading={STRINGS.hoursOfOperation}
            description={Array.isArray(attraction.hours) ? attraction.hours.join(", ") : attraction.hours}
            icon={faClock}
            size={iconSize}
            iconColor={COLORS.primary}
            FontAwesomeIcon={FontAwesomeIcon}
          />
        )}
      </View>
      <DetailDescription
        title={STRINGS.attractionsDescTitle}
        description={attraction?.description?.split("\\n").join("\n") || "No additional details provided."}
        FontAwesomeIcon={FontAwesomeIcon}
        iconSize={iconSize}
      />
      {attraction?.tips && <Heading fontSize={18} text={STRINGS.tipsTitle} color={COLORS.textDark} />}
    </View>
  );

  return (
    <View style={styles.container}>
      <DetailList
        data={attraction?.tips}
        FontAwesomeIcon={FontAwesomeIcon}
        iconSize={Platform.OS == "web" ? "xl" : iconSize}
        icon={faCircleInfo}
        renderItemDetails={renderItemDetails}
        headerComponent={HeaderComponent}
      />
    </View>
  );
};
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  cardsContainer: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  chipContainer: {
    paddingTop: 12,
  },
  parkingName: {
    fontSize: 16,
    fontWeight: Platform.OS == "web" ? 500 : "bold",
    color: COLORS.textDark,
    lineHeight: 24,
    marginBottom: 4,
  },
  parkingDetails: {
    fontSize: 16,
    color: COLORS.border,
    lineHeight: 28,
  },
  dotStyle: {
    fontSize: 18,
    color: COLORS.border,
  },
  mapContainer: {
    height: Platform.OS === "web" ? "300px" : 250,
    marginBottom: 24,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 8,
    justifyContent: "flex-end",
  },
  headerTitle: {
    display: "none",
  },
});

export default AttractionsDetailViewCommon;
